import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useEffect, useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {RegistryLabelingStrategy, TagSchema} from 'constants/RegistryConstants';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getTransformDataTypes} from 'instruments/connections/ConnectionDAO';
import {getStorableSchemaSettingsUrl, getTagSchemaSettingsUrl} from 'registry/RegistryUtil';
import {AssayRunSchema, ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {assaySchemaURL} from 'results/assay-schemas/AssaySchemaDAO';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import InfoIcon from 'ui-library/icons/InfoIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle, Size} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {capitalize, constantCaseToWords} from 'util/NameUtil';
import {Optional} from 'util/Types';

import {
  automationFileConfigStyles,
  AutomationOutputFileConfigEditorProps,
  SectionNumber,
} from '../AutomationFileConfigUtils';
import {EditComponentButton} from '../RunSchemaEditorActionButton';
import {
  ClientAutomationOutputFileConfigSpec,
  AutomationOutputFileEntitySchemaInfo,
  AutomationOutputFileResultSchemaInfo,
  AutomationOutputFileTableType,
  AutomationOutputFileTableSortType,
  labAutomationStructuredTableTypeOptions,
  labAutomationStructuredTableSortTypeOptions,
  OutputFileAction,
  TagSchemaInfoActionType,
  AutomationOutputFileDestinationInfo,
  INVENTORY_ACTION_TYPES,
  isTransfer,
  REGISTRATION_ACTION_TYPES,
  RESULTS_ACTION_TYPES,
  DestinationInfoAction,
  AutomationOutputFileTransformDataType,
} from '../RunSchemaSpecConstants';
import {AOFConfigRegistriesQuery} from './__generated/AOFConfigRegistriesQuery';
import {
  ConnectionSchemaAdapterIdQuery,
  ConnectionSchemaAdapterIdQueryVariables,
} from './__generated/ConnectionSchemaAdapterIdQuery';
import {
  DestinationInfoSectionQuery,
  DestinationInfoSectionQueryVariables,
} from './__generated/DestinationInfoSectionQuery';
import {
  EntitySchemaInfoSectionQuery,
  EntitySchemaInfoSectionQueryVariables,
} from './__generated/EntitySchemaInfoSectionQuery';
import {
  ResultSchemaInfoSectionQuery,
  ResultSchemaInfoSectionQueryVariables,
} from './__generated/ResultSchemaInfoSectionQuery';
import {convertResultSchemaToDatasetConfigString} from './DatasetConfigConstants';
import DestinationInfoModal from './DestinationInfoModal';
import EntitySchemaInfoModal from './EntitySchemaInfoModal';
import ResultSchemaInfoModal from './ResultSchemaInfoModal';

type ConnectionSchemaAdapterIdNode = ExtractByTypename<
  ConnectionSchemaAdapterIdQuery['connectionSchema'],
  'ConnectionSchemaNode'
>;

type Registry = AOFConfigRegistriesQuery['registries'][number];

function EditableSchemaDisplay(props: {
  schemaName: string;
  schemaUrl?: string;
  onEdit: () => void;
}): JSX.Element {
  const [hideActionButtons, setHideActionButtons] = useState(true);

  return (
    <div
      onMouseEnter={() => {
        setHideActionButtons(false);
      }}
      onMouseLeave={() => {
        setHideActionButtons(true);
      }}
      className={css(styles.schemaDisplayContainer, !hideActionButtons ? styles.active : null)}
    >
      <div className={css(styles.schemaName)}>
        {props.schemaUrl ? <a href={props.schemaUrl}>{props.schemaName}</a> : props.schemaName}
      </div>
      <div className={css(styles.schemaActionButtons, hideActionButtons ? styles.hidden : null)}>
        <EditComponentButton label="Edit schema info" onClick={props.onEdit} />
      </div>
    </div>
  );
}

function SchemaInfoSection(props: {
  schemaName: string;
  schemaUrl?: string;
  hasSchema: boolean;
  isLoading: boolean;
  openModal: () => void;
  label: string;
  emptyText: string;
}): JSX.Element {
  return (
    <div className={css(styles.schemaInfoSection)}>
      <FieldLabel label={props.label} labelStyle={LabelStyle.LABEL} isRequired={true} />
      <br />
      {props.isLoading ? (
        <CenteredHorizontalThrobber />
      ) : props.hasSchema ? (
        <EditableSchemaDisplay
          schemaName={props.schemaName}
          schemaUrl={props.schemaUrl}
          onEdit={props.openModal}
        />
      ) : (
        <div className={css(styles.schemaDisplayContainer)}>
          <a className={css(styles.schemaName)} onClick={props.openModal}>
            {props.emptyText}
          </a>
        </div>
      )}
    </div>
  );
}

export const ENTITY_SCHEMA_INFO_SECTION_QUERY = gql`
  query EntitySchemaInfoSectionQuery($tagSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: LAB_AUTOMATION) {
    tagSchema(id: $tagSchemaId) {
      ... on TagSchemaNode {
        ...TagSchemaGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function EntitySchemaInfoSection(
  props: AutomationOutputFileConfigEditorProps & QueryProps
): JSX.Element | null {
  const entitySchemaInfo = props.outputFileConfig.entitySchemaInfo;
  const response = useSkippableBenchlingQuery<
    EntitySchemaInfoSectionQuery,
    EntitySchemaInfoSectionQueryVariables
  >(ENTITY_SCHEMA_INFO_SECTION_QUERY, {
    variables: {tagSchemaId: entitySchemaInfo?.schema!},
    skip: !entitySchemaInfo?.schema,
  });

  if (!entitySchemaInfo) {
    return null;
  }
  let schema: TagSchema | null = null;
  if (response.type === QueryResultType.SUCCESS) {
    const schemaGql = response.data.tagSchema;
    schema = !isInaccessible(schemaGql) ? graphQLToREST('TagSchema', schemaGql) : null;
  }

  const schemaName = schema ? schema.name : 'Deleted Schema';
  return (
    <SchemaInfoSection
      schemaName={schemaName}
      schemaUrl={
        schema && props.registriesById[schema.registryId]
          ? getTagSchemaSettingsUrl(
              props.registriesById[schema.registryId].organization.urlIdentifier,
              schema.registryId,
              schema.id
            )
          : undefined
      }
      isLoading={response.type === QueryResultType.PENDING}
      hasSchema={schema != null}
      openModal={() => {
        legacyRunModal(EntitySchemaInfoModal, props);
      }}
      label="Entity schema"
      emptyText="Configure entity schema"
    />
  );
}

export const RESULT_SCHEMA_INFO_SECTION_QUERY = gql`
  query ResultSchemaInfoSectionQuery($resultSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: LAB_AUTOMATION) {
    resultSchema(id: $resultSchemaId) {
      ... on ResultSchemaNode {
        ...ResultSchemaGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ResultSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function ResultSchemaInfoSection(props: {
  editorProps: AutomationOutputFileConfigEditorProps;
  schema: ResultSchema | null;
  responseType: QueryResultType;
}): JSX.Element | null {
  if (!props.editorProps.outputFileConfig.resultSchemaInfo) {
    return null;
  }

  const schemaName = props.schema ? props.schema.name : 'Deleted Schema';
  return (
    <SchemaInfoSection
      schemaName={schemaName}
      isLoading={props.responseType === QueryResultType.PENDING}
      hasSchema={props.schema != null}
      schemaUrl={
        props.schema ? assaySchemaURL(LegacyAssaySchemaType.ASSAY_RESULT, props.schema.id) : undefined
      }
      openModal={() => {
        legacyRunModal(ResultSchemaInfoModal, props.editorProps);
      }}
      label="Result schema"
      emptyText="Configure result schema"
    />
  );
}

export const DESTINATION_INFO_SECTION_QUERY = gql`
  query DestinationInfoSectionQuery($storableSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: LAB_AUTOMATION) {
    storableSchema(id: $storableSchemaId) {
      ... on StorableSchemaInterface {
        ...StorableSchemaGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('StorableSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function DestinationInfoSection(props: {
  actionType: OutputFileAction;
  destinationInfo: Optional<AutomationOutputFileDestinationInfo>;
  onUpdate: (destinationInfo: AutomationOutputFileDestinationInfo | null) => void;
  destinationInfoActionType: DestinationInfoAction | undefined;
  registriesById: {[id: string]: Registry};
  shouldPerformPlateTransfer: boolean;
}): JSX.Element | null {
  const destinationSchemaId =
    props.destinationInfo?.containerSchema ||
    props.destinationInfo?.plateSchema ||
    props.destinationInfo?.boxSchema;
  const response = useSkippableBenchlingQuery<
    DestinationInfoSectionQuery,
    DestinationInfoSectionQueryVariables
  >(DESTINATION_INFO_SECTION_QUERY, {
    variables: {storableSchemaId: destinationSchemaId!},
    skip: !destinationSchemaId,
  });

  if (!isTransfer(props.actionType, props.shouldPerformPlateTransfer)) {
    return null;
  }
  function openModal(): void {
    legacyRunModal(DestinationInfoModal, {
      initialActionType: props.destinationInfoActionType,
      initialDestinationInfo: props.destinationInfo || getDefaultDestinationInfo(),
      onRequestSubmit: (destinationInfo: AutomationOutputFileDestinationInfo | null) => {
        props.onUpdate(destinationInfo);
      },
    });
  }
  let schema: StorableSchema | null = null;
  if (response.type === QueryResultType.SUCCESS) {
    const schemaGql = response.data.storableSchema;
    schema = !isInaccessible(schemaGql) ? graphQLToREST('StorableSchema', schemaGql) : null;
  }
  const schemaName = schema ? schema.name : 'Deleted Schema';
  const displayName =
    props.destinationInfoActionType === DestinationInfoAction.USE_EXISTING
      ? 'Use existing destinations'
      : schemaName;
  return (
    <SchemaInfoSection
      schemaName={displayName}
      schemaUrl={
        schema && props.registriesById[schema.registryId]
          ? getStorableSchemaSettingsUrl(
              props.registriesById[schema.registryId].organization.urlIdentifier,
              schema.registryId,
              schema.id
            )
          : undefined
      }
      isLoading={response.type === QueryResultType.PENDING}
      hasSchema={props.destinationInfoActionType != null}
      openModal={openModal}
      label="Inventory schema"
      emptyText="Configure inventory schema"
    />
  );
}

interface QueryProps {
  registriesById: {[id: string]: Registry};
}

export function getDefaultEntitySchemaInfo(): AutomationOutputFileEntitySchemaInfo {
  return {
    schema: null,
    labelingStrategy: RegistryLabelingStrategy.DELETE_NAMES,
    actionType: TagSchemaInfoActionType.CREATE,
    fields: {},
  };
}

function getDefaultResultSchemaInfo(): AutomationOutputFileResultSchemaInfo {
  return {
    schema: null,
    fields: {},
  };
}

function getDefaultDestinationInfo(): AutomationOutputFileDestinationInfo {
  return {
    plateSchema: null,
    fields: {},
  };
}

export function updateOutputFileConfigForActionSelection(
  outputFileConfig: ClientAutomationOutputFileConfigSpec,
  action: OutputFileAction,
  shouldCreateDataset: boolean,
  shouldAllowAttachment: boolean,
  shouldPerformPlateTransfer: boolean,
  justCheckedKey?: keyof CheckedActions,
  justCheckedValue?: boolean,
  resultSchema?: ResultSchema | null
): ClientAutomationOutputFileConfigSpec {
  let entitySchemaInfo = null;
  if (REGISTRATION_ACTION_TYPES.has(action)) {
    entitySchemaInfo = outputFileConfig.entitySchemaInfo || getDefaultEntitySchemaInfo();
    if (!isTransfer(action, shouldPerformPlateTransfer)) {
      delete entitySchemaInfo.shouldReplaceExisting;
    }
  }

  let resultSchemaInfo = null;
  if (RESULTS_ACTION_TYPES.has(action)) {
    resultSchemaInfo = outputFileConfig.resultSchemaInfo || getDefaultResultSchemaInfo();
  }

  let datasetConfigJson = outputFileConfig.datasetConfigJson;
  let analysisTemplate = outputFileConfig.analysisTemplate;
  if (justCheckedKey === 'dataset') {
    if (justCheckedValue) {
      if (resultSchema) {
        datasetConfigJson = convertResultSchemaToDatasetConfigString(resultSchema);
      }
    } else {
      // removes dataset config and analysis template if 'Record dataset' is unchecked
      datasetConfigJson = undefined;
      analysisTemplate = null;
    }
  }

  return {
    ...outputFileConfig,
    entitySchemaInfo,
    resultSchemaInfo,
    actionType: action,
    shouldCreateDataset,
    shouldAllowAttachment,
    shouldPerformPlateTransfer,
    datasetConfigJson,
    analysisTemplate,
  };
}

interface CheckedActions {
  register: boolean;
  transfer: boolean;
  results: boolean;
  dataset: boolean;
  attachments: boolean;
  plateTransfer: boolean;
}

const labAutomationOutputFileActionOptions: {[key in keyof CheckedActions]: string} = {
  register: 'Register and/or update entities',
  transfer: 'Transfer samples',
  results: 'Record results',
  dataset: 'Record dataset',
  attachments: 'Attach additional files',
  plateTransfer: 'Perform plate transfer',
};

const labAutomationOutputFileActionTooltips: {[key in keyof CheckedActions]: string | null} = {
  register: null,
  transfer: null,
  results:
    'Results are recommended for processing final data. They require additional result schema configuration and will appear in structured Results tables.',
  dataset:
    'Datasets are recommended for processing raw and partially processed data. They do not require schema configuration and appear in Benchling as tabular data.',
  attachments: 'If set, allow selecting files to attach directly to the run.',
  plateTransfer:
    'Performs transfer using the plate map tool. Only one of "Transfer samples" or "Perform plate transfer" can be selected.',
};

export function ActionSelector(props: {
  connectionSchema: ConnectionSchemaAdapterIdNode | null;
  resultSchema: ResultSchema | null;
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  onUpdateConfig: (newConfig: ClientAutomationOutputFileConfigSpec) => void;
}): JSX.Element {
  const [checked, setChecked] = useState({
    register: REGISTRATION_ACTION_TYPES.has(props.outputFileConfig.actionType),
    transfer: INVENTORY_ACTION_TYPES.has(props.outputFileConfig.actionType),
    results: RESULTS_ACTION_TYPES.has(props.outputFileConfig.actionType),
    dataset: props.outputFileConfig.shouldCreateDataset ?? false,
    attachments: props.outputFileConfig.shouldAllowAttachment ?? false,
    plateTransfer: props.outputFileConfig.shouldPerformPlateTransfer ?? false,
  });
  function getActionTypeFromCheckedBoxes(checkedActions: CheckedActions): OutputFileAction {
    if (checkedActions.results) {
      if (checkedActions.register) {
        return checkedActions.transfer
          ? OutputFileAction.TRANSFORMATION_TRANSFER_AND_RESULTS
          : OutputFileAction.REGISTRATION_AND_RESULTS;
      }
      return checkedActions.transfer ? OutputFileAction.TRANSFER_AND_RESULTS : OutputFileAction.PURE_RESULTS;
    }
    if (checkedActions.register) {
      return checkedActions.transfer
        ? OutputFileAction.TRANSFORMATION_TRANSFER
        : OutputFileAction.PURE_REGISTRATION;
    }
    if (checkedActions.transfer) {
      return OutputFileAction.PURE_TRANSFER;
    }
    return OutputFileAction.NO_ACTION;
  }
  function handleUpdate(
    newChecked: CheckedActions,
    justCheckedKey: keyof CheckedActions,
    justCheckedValue: boolean
  ): void {
    setChecked(newChecked);
    const newConfig = updateOutputFileConfigForActionSelection(
      props.outputFileConfig,
      getActionTypeFromCheckedBoxes(newChecked),
      newChecked.dataset,
      newChecked.attachments,
      newChecked.plateTransfer,
      justCheckedKey,
      justCheckedValue,
      props.resultSchema
    );
    props.onUpdateConfig(newConfig);
  }
  function isOnlyCheckedBox(key: keyof CheckedActions): boolean {
    for (const k of Object.keys(checked) as Array<keyof CheckedActions>) {
      // Attaching additional files does not count towards min of one action requirement.
      if (k !== key && k !== 'attachments' && checked[k]) {
        return false;
      } else if (k === key && !checked[k]) {
        return false;
      }
    }
    return true;
  }

  function isOtherTransferChecked(key: keyof CheckedActions): boolean {
    const transferActions = ['transfer', 'plateTransfer'];
    // this function is only relevant for the above two checkboxes
    if (!transferActions.includes(key)) {
      return false;
    }
    return (key === 'transfer' && checked.plateTransfer) || (key === 'plateTransfer' && checked.transfer);
  }

  function isCheckboxDisabled(key: keyof CheckedActions): boolean {
    return isOnlyCheckedBox(key) || isOtherTransferChecked(key);
  }

  function tooltipText(key: keyof CheckedActions): string {
    if (isOnlyCheckedBox(key)) {
      return 'At least one action other than attaching additional files must be selected.';
    } else if (isOtherTransferChecked(key)) {
      return 'Only one of "Transfer samples" or "Perform plate transfer" can be selected';
    }
    return ''; // tooltip should be disabled in this case regardless
  }

  let keysToUse = BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS
    ? Object.keys(checked)
    : Object.keys(checked).filter((key) => key !== 'dataset');
  keysToUse = BenchlingConfig.LAB_AUTOMATION_ENABLE_PLATE_TRANSFER
    ? keysToUse
    : keysToUse.filter((key) => key !== 'plateTransfer');

  return (
    <>
      {keysToUse.map((key: keyof CheckedActions) => (
        <div key={key} className={css(styles.actionCheckbox)}>
          <Tooltip key={key} title={tooltipText(key)} disabled={!isCheckboxDisabled(key)}>
            <CheckboxInput
              className={css(styles.checkboxInput)}
              checked={checked[key]}
              id={key}
              label={labAutomationOutputFileActionOptions[key]}
              onChange={(e) => {
                handleUpdate({...checked, [key]: e.target.checked}, key, e.target.checked);
              }}
              disabled={isCheckboxDisabled(key)}
              dataTestAttrs={{component: 'ActionSelector', element: 'actionOption', key}}
            />
            {labAutomationOutputFileActionTooltips[key] && (
              <div className="u-mini-spacing-left">
                <Popover
                  renderContent={() => <span>{labAutomationOutputFileActionTooltips[key]}</span>}
                  placement={FloatingPlacement.TOP}
                  interaction={FloatingInteraction.HOVER}
                >
                  <InfoIcon />
                </Popover>
              </div>
            )}
          </Tooltip>
        </div>
      ))}
    </>
  );
}

export function TableTypeSelector(props: {
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  onUpdateConfig: (newConfig: ClientAutomationOutputFileConfigSpec) => void;
}): JSX.Element {
  return (
    <SelectDropdown
      displayName={labAutomationStructuredTableTypeOptions[props.outputFileConfig.tableType]}
      menu={
        <Menu
          value={props.outputFileConfig.tableType}
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FULL}
          scrollable={true}
        >
          {Object.entries(labAutomationStructuredTableTypeOptions).map(
            ([value, display]: [AutomationOutputFileTableType, string]) => (
              <Option
                key={value}
                value={value}
                onSelect={() => {
                  props.onUpdateConfig({
                    ...props.outputFileConfig,
                    tableType: value,
                  });
                }}
              >
                {display}
              </Option>
            )
          )}
        </Menu>
      }
    />
  );
}

export function TableSortTypeSelector(props: {
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  onUpdateConfig: (newConfig: ClientAutomationOutputFileConfigSpec) => void;
}): JSX.Element {
  return (
    <SelectDropdown
      displayName={labAutomationStructuredTableSortTypeOptions[props.outputFileConfig.tableSortType]}
      menu={
        <Menu
          value={props.outputFileConfig.tableSortType}
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FULL}
          scrollable={true}
        >
          {Object.entries(labAutomationStructuredTableSortTypeOptions).map(
            ([value, display]: [AutomationOutputFileTableSortType, string]) => (
              <Option
                key={value}
                value={value}
                onSelect={() => {
                  props.onUpdateConfig({
                    ...props.outputFileConfig,
                    tableSortType: value,
                  });
                }}
              >
                {display}
              </Option>
            )
          )}
        </Menu>
      }
    />
  );
}

export const CONNECTION_SCHEMA_ADAPTER_ID_QUERY = gql`
  query ConnectionSchemaAdapterIdQuery($connectionSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: INSTRUMENTS) {
    connectionSchema(id: $connectionSchemaId) {
      ... on ConnectionSchemaNode {
        id
        integrationAdapterId
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function TransformDataTypeSelector(props: {
  baseSchema: AssayRunSchema;
  connectionSchema: ConnectionSchemaAdapterIdNode | null;
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  onUpdateConfig: (newConfig: ClientAutomationOutputFileConfigSpec) => void;
}): JSX.Element {
  const [transformDataTypes, setTransformDataTypes] = useState(null as {[id: string]: string} | null);
  useEffect(() => {
    if (props.connectionSchema) {
      getTransformDataTypes(props.connectionSchema.integrationAdapterId).then((types) => {
        let transformTypes = types;
        if (BenchlingConfig.ENABLE_BENCHLING_CONNECT_JSON_MAPPER_CONFIG) {
          transformTypes = {
            ...transformTypes,
            CUSTOM: 'configure custom JSON to CSV mapping',
          };
        }
        setTransformDataTypes(transformTypes);
      });
    } else {
      setTransformDataTypes(null);
    }
  }, [props.connectionSchema]);

  let displayValue: string | null = null;

  if (transformDataTypes) {
    const propTransformDataType = props.outputFileConfig.transformDataType;
    const keys = Object.keys(transformDataTypes);
    if (propTransformDataType && keys.includes(propTransformDataType)) {
      displayValue = propTransformDataType;
    } else if (keys.length > 0 && keys.length <= 2) {
      // If there's only one transform type (and Custom), set that single transform type as the default
      // Custom should always be the second option, but check just to be safe.
      const defaultTransformType = keys.length === 1 || keys[0] !== 'Custom' ? keys[0] : keys[1];
      displayValue = defaultTransformType;
      props.onUpdateConfig({
        ...props.outputFileConfig,
        transformDataType: defaultTransformType as AutomationOutputFileTransformDataType,
      });
    } else {
      displayValue = null;
      props.outputFileConfig.transformDataType = null;
    }
  }

  return transformDataTypes ? (
    <SelectDropdown
      displayName={displayValue != null ? capitalize(constantCaseToWords(displayValue)) : 'Select an option'}
      menu={
        <Menu value={displayValue} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} scrollable={true}>
          {Object.entries(transformDataTypes).map(
            ([value, description]: [AutomationOutputFileTransformDataType, string]) => (
              <Option
                key={value}
                value={value}
                onSelect={() => {
                  props.onUpdateConfig({
                    ...props.outputFileConfig,
                    transformDataType: value,
                  });
                }}
                tooltipTitle={description}
                tooltipPlacement={FloatingPlacement.LEFT}
              >
                {capitalize(constantCaseToWords(value))}
              </Option>
            )
          )}
        </Menu>
      }
    />
  ) : (
    <CenteredHorizontalThrobber />
  );
}

function AutomationOutputFileConfigEditorSchemaInfoSection(
  props: AutomationOutputFileConfigEditorProps & QueryProps
): JSX.Element {
  const connectionSchemaResult = useSkippableBenchlingQuery<
    ConnectionSchemaAdapterIdQuery,
    ConnectionSchemaAdapterIdQueryVariables
  >(CONNECTION_SCHEMA_ADAPTER_ID_QUERY, {
    variables: {connectionSchemaId: props.connectionSchemaId!},
    skip: !props.connectionSchemaId,
    renderCachedDataWhenRefetching: true,
  });

  const connectionSchema: ConnectionSchemaAdapterIdNode | null = useMemo(() => {
    if (connectionSchemaResult.type === QueryResultType.SUCCESS) {
      const connectionSchemaOrInaccessible = connectionSchemaResult.data.connectionSchema;
      if (!isInaccessible(connectionSchemaOrInaccessible)) {
        return connectionSchemaOrInaccessible;
      } else {
        return null;
      }
    }
    return null;
  }, [connectionSchemaResult]);

  const resultSchemaInfo = props.outputFileConfig.resultSchemaInfo;
  const resultSchemaResponse = useSkippableBenchlingQuery<
    ResultSchemaInfoSectionQuery,
    ResultSchemaInfoSectionQueryVariables
  >(RESULT_SCHEMA_INFO_SECTION_QUERY, {
    variables: {resultSchemaId: resultSchemaInfo?.schema!},
    skip: !resultSchemaInfo?.schema,
  });

  let resultSchema: ResultSchema | null = null;
  if (resultSchemaResponse.type === QueryResultType.SUCCESS) {
    const schemaGql = resultSchemaResponse.data.resultSchema;
    resultSchema = schemaGql && !isInaccessible(schemaGql) ? graphQLToREST('ResultSchema', schemaGql) : null;
  }

  return (
    <div className={css(automationFileConfigStyles.subsection, automationFileConfigStyles.subsectionBorder)}>
      <SectionNumber sectionNumber={props.sectionNumber} />
      <div className={css(automationFileConfigStyles.innerTitle)}>Configure output table</div>
      <div
        className={css(automationFileConfigStyles.description, automationFileConfigStyles.sectionDescription)}
      >
        Tell Benchling how to process your data. Specify schemas if creating new items.
      </div>
      <div className={css(styles.schemaInfoSectionRow)}>
        <div className={css(styles.outputFileConfigActionDropdown)}>
          <FieldLabel label="Benchling action" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <br />
          <ActionSelector
            connectionSchema={connectionSchema}
            resultSchema={resultSchema}
            outputFileConfig={props.outputFileConfig}
            onUpdateConfig={props.onUpdateConfig}
          />
        </div>
        <div className={css(styles.tableTypeDropdown)}>
          <FieldLabel label="Output table display" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <br />
          <TableTypeSelector
            outputFileConfig={props.outputFileConfig}
            onUpdateConfig={props.onUpdateConfig}
          />
        </div>
        <div className={css(styles.tableTypeDropdown)}>
          <FieldLabel label="Output table sorting" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <br />
          <TableSortTypeSelector
            outputFileConfig={props.outputFileConfig}
            onUpdateConfig={props.onUpdateConfig}
          />
        </div>
        {connectionSchema && (
          <div className={css(styles.tableTypeDropdown)}>
            <FieldLabel label="Instrument data type" labelStyle={LabelStyle.LABEL} isRequired={true} />
            <br />
            <TransformDataTypeSelector
              baseSchema={props.baseSchema}
              connectionSchema={connectionSchema}
              outputFileConfig={props.outputFileConfig}
              onUpdateConfig={props.onUpdateConfig}
            />
          </div>
        )}
        <div className={css(styles.tableTypeDropdown)}>
          <TextField
            isRequired={false}
            autoFocus={props.outputFileConfig.tabName === ''}
            label="Choose tab to process"
            labelStyle={LabelStyle.LABEL}
            value={props.outputFileConfig.tabName || ''}
            size={Size.MEDIUM}
            onChange={(e) => {
              props.onUpdateConfig({
                ...props.outputFileConfig,
                tabName: e.target.value,
              });
            }}
            placeholder="Tab name"
            help="If not specified, the first tab of the file will be processed by default"
            helpInteraction={FloatingInteraction.HOVER}
          />
        </div>
      </div>
      <div className={css(styles.schemaInfoSectionRow)}>
        <EntitySchemaInfoSection {...props} />
        <DestinationInfoSection
          actionType={props.outputFileConfig.actionType}
          destinationInfo={props.outputFileConfig.destinationInfo}
          destinationInfoActionType={props.outputFileConfig.destinationActionType}
          onUpdate={(destinationInfo: AutomationOutputFileDestinationInfo | null) => {
            if (destinationInfo) {
              props.onUpdateConfig({
                ...props.outputFileConfig,
                destinationInfo,
                destinationActionType: DestinationInfoAction.CREATE_OR_UPDATE,
              });
            } else {
              const newConfig = {
                ...props.outputFileConfig,
                destinationActionType: DestinationInfoAction.USE_EXISTING,
              };
              delete newConfig.destinationInfo;
              props.onUpdateConfig(newConfig);
            }
          }}
          registriesById={props.registriesById}
          shouldPerformPlateTransfer={props.outputFileConfig.shouldPerformPlateTransfer}
        />
        <ResultSchemaInfoSection
          editorProps={props}
          schema={resultSchema}
          responseType={resultSchemaResponse.type}
        />
      </div>
    </div>
  );
}

export const AOF_CONFIG_REGISTRIES_QUERY: DocumentNode = gql`
  query AOFConfigRegistriesQuery @skipAudit {
    registries {
      id
      organization {
        id
        urlIdentifier
      }
    }
  }
`;

export default function QueryingAutomationOutputFileConfigEditorSchemaInfoSection(
  props: AutomationOutputFileConfigEditorProps
): JSX.Element | null {
  const queryResult = useBenchlingQuery<AOFConfigRegistriesQuery>(AOF_CONFIG_REGISTRIES_QUERY);
  const registriesById: {[id: string]: Registry} = {};

  if (queryResult.type === QueryResultType.SUCCESS) {
    for (const registry of queryResult.data.registries) {
      registriesById[registry.id] = registry;
    }
  }
  return <AutomationOutputFileConfigEditorSchemaInfoSection {...props} registriesById={registriesById} />;
}

const styles = StyleSheet.create({
  schemaInfoSectionRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: '28px',
  },
  outputFileConfigActionDropdown: {
    flex: 0.33,
    marginRight: '32px',
  },
  tableTypeDropdown: {
    flex: 0.33,
    marginRight: '32px',
  },
  schemaActionButtons: {
    display: 'flex',
    flex: 0.2,
    justifyContent: 'flex-end',
    marginTop: '4px',
  },
  schemaDisplayContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginLeft: '-8px',
    alignItems: 'flex-start',
  },
  schemaName: {
    wordWrap: 'break-word',
    overflowWrap: 'anywhere',
    paddingLeft: '8px',
    paddingTop: '6px',
    paddingBottom: '6px',
    flex: 0.8,
  },
  active: {
    background: Colors.GRAY_LIGHTER,
  },
  hidden: {
    display: 'none',
  },
  schemaInfoSection: {
    flex: 0.33,
    marginRight: '32px',
  },
  actionCheckbox: {
    alignItems: 'center',
    display: 'flex',
  },
  checkboxInput: {
    marginBottom: '2px',
    width: '100%',
  },
});
